import * as React from 'react';
import { type VariantProps, cva } from 'class-variance-authority';
import { CircleIcon } from 'lucide-react';
import { RadioGroup as RadioGroupPrimitive } from 'radix-ui';

import { cn } from '@/lib/utils';

const radioGroupVariants = cva('', {
  variants: {
    orientation: {
      vertical: 'grid gap-4',
      horizontal: 'flex flex-wrap gap-8',
    },
  },
  defaultVariants: {
    orientation: 'horizontal',
  },
});

function RadioGroup({
  className,
  orientation,
  ...props
}: React.ComponentProps<typeof RadioGroupPrimitive.Root> &
  VariantProps<typeof radioGroupVariants>) {
  return (
    <RadioGroupPrimitive.Root
      data-slot="radio-group"
      className={cn(radioGroupVariants({ orientation, className }))}
      {...props}
    />
  );
}

function RadioGroupItem({
  className,
  ...props
}: React.ComponentProps<typeof RadioGroupPrimitive.Item>) {
  return (
    <RadioGroupPrimitive.Item
      data-slot="radio-group-item"
      className={cn(
        // Base styles
        'border-primary fill-primary text-primary aspect-square size-5 shrink-0 rounded-full border bg-white shadow-xs transition-shadow outline-none',

        // Checked state
        'data-[state=checked]:fill-hydrangea-500 data-[state=checked]:text-hydrangea-500 data-[state=checked]:border-hydrangea-500',

        // Focus state
        'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]',

        // Invalid/error state
        'aria-invalid:border-destructive aria-invalid:ring-destructive/20',

        // Disabled state
        'disabled:cursor-not-allowed disabled:opacity-50',

        // Custom className override
        className
      )}
      {...props}
    >
      <RadioGroupPrimitive.Indicator
        data-slot="radio-group-indicator"
        className="relative flex items-center justify-center"
      >
        <CircleIcon className="absolute top-1/2 left-1/2 size-3 -translate-x-1/2 -translate-y-1/2 fill-current" />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  );
}

export { RadioGroup, RadioGroupItem, radioGroupVariants };
